/**
 * MaintenanceTab — cache cleanup and maintenance operations.
 * Extracted verbatim from AdminScreen.jsx (god-file decomposition).
 */
import React, { useEffect, useState } from 'react';
import { View, Text, TouchableOpacity, TextInput, Switch } from 'react-native';
import { db } from '../firebaseConfig';
import { doc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { adminStyles as styles } from './adminStyles';

export default function MaintenanceTab() {
  const [comingSoon, setComingSoon] = useState(false);
  const [accessStage, setAccessStageState] = useState('live'); // 'alpha' | 'beta' | 'live'
  const [banner, setBanner] = useState(false);
  const [startedAt, setStartedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Community settings
  const [communityModalEnabled, setCommunityModalEnabled] = useState(true);
  const [communityRedirectUrl, setCommunityRedirectUrl] = useState('https://discord.gg/your-discord-link');
  const [communityScreenEnabled, setCommunityScreenEnabled] = useState(false);
  
  // SGP Optimizer Coming Soon settings
  const [sgpComingSoonEnabled, setSgpComingSoonEnabled] = useState(false);
  // Photo upload (image-to-query) feature flag
  const [photoUploadEnabled, setPhotoUploadEnabledState] = useState(false);

  useEffect(() => {
    const ref = doc(db, 'site_maintenance', 'global');
    const unsub = onSnapshot(ref, (snap) => {
      const d = snap.data() || {};
      setComingSoon(!!d.comingSoonEnabled);
      setAccessStageState(d.accessStage || 'live');
      setBanner(!!d.maintenanceBannerEnabled);
      setStartedAt(d.maintenanceBannerStartedAt || null);
      setCommunityModalEnabled(d.communityModalEnabled !== false); // Default to true
      setCommunityRedirectUrl(d.communityRedirectUrl || 'https://discord.gg/your-discord-link');
      setCommunityScreenEnabled(d.communityScreenEnabled === true);
      setSgpComingSoonEnabled(!!d.sgpComingSoonEnabled);
      setPhotoUploadEnabledState(!!d.photoUploadEnabled);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const setAccessStage = async (stage) => {
    const ref = doc(db, 'site_maintenance', 'global');
    try {
      // Setting 'live' also turns off comingSoonEnabled so they're in sync
      const payload = { accessStage: stage, updatedAt: serverTimestamp() };
      if (stage === 'live') payload.comingSoonEnabled = false;
      else if (stage === 'alpha' || stage === 'beta') payload.comingSoonEnabled = true;
      await setDoc(ref, payload, { merge: true });
    } catch (e) {
      console.warn('Failed to set access stage', e);
    }
  };

  const toggleComingSoon = async () => {
    const ref = doc(db, 'site_maintenance', 'global');
    try {
      await setDoc(ref, { comingSoonEnabled: !comingSoon, updatedAt: serverTimestamp() }, { merge: true });
    } catch (e) {
      console.warn('Failed to toggle coming soon', e);
    }
  };

  const toggleBanner = async () => {
    const ref = doc(db, 'site_maintenance', 'global');
    try {
      const payload = { maintenanceBannerEnabled: !banner, updatedAt: serverTimestamp() };
      if (!banner && !startedAt) payload.maintenanceBannerStartedAt = serverTimestamp();
      await setDoc(ref, payload, { merge: true });
    } catch (e) {
      console.warn('Failed to toggle banner', e);
    }
  };

  const toggleCommunityModal = async () => {
    const ref = doc(db, 'site_maintenance', 'global');
    try {
      await setDoc(ref, { communityModalEnabled: !communityModalEnabled, updatedAt: serverTimestamp() }, { merge: true });
    } catch (e) {
      console.warn('Failed to toggle community modal', e);
    }
  };

  const toggleCommunityScreen = async () => {
    const ref = doc(db, 'site_maintenance', 'global');
    try {
      await setDoc(ref, { communityScreenEnabled: !communityScreenEnabled, updatedAt: serverTimestamp() }, { merge: true });
    } catch (e) {
      console.warn('Failed to toggle community screen', e);
    }
  };

  const updateCommunityUrl = async () => {
    const ref = doc(db, 'site_maintenance', 'global');
    try {
      await setDoc(ref, { communityRedirectUrl: communityRedirectUrl, updatedAt: serverTimestamp() }, { merge: true });
    } catch (e) {
      console.warn('Failed to update community URL', e);
    }
  };

  const toggleSgpComingSoon = async () => {
    const ref = doc(db, 'site_maintenance', 'global');
    try {
      await setDoc(ref, { sgpComingSoonEnabled: !sgpComingSoonEnabled, updatedAt: serverTimestamp() }, { merge: true });
    } catch (e) {
      console.warn('Failed to toggle SGP coming soon', e);
    }
  };

  const togglePhotoUpload = async () => {
    const ref = doc(db, 'site_maintenance', 'global');
    try {
      await setDoc(ref, { photoUploadEnabled: !photoUploadEnabled, updatedAt: serverTimestamp() }, { merge: true });
    } catch (e) {
      console.warn('Failed to toggle photo upload', e);
    }
  };


  return (
    <View style={styles.tabWrap}>
      <Text style={styles.sectionTitle}>Maintenance Controls</Text>

      {/* Access Stage - replaces the binary coming soon toggle */}
      <View style={{ marginBottom: 20, padding: 16, backgroundColor: 'rgba(255,255,255,0.04)', borderRadius: 12, borderWidth: 1, borderColor: 'rgba(255,255,255,0.1)' }}>
        <Text style={[styles.label, { marginBottom: 4 }]}>Access Stage</Text>
        <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, marginBottom: 12 }}>
          Alpha: admin + alpha testers only{'\n'}Beta: admin, alpha + beta testers{'\n'}Live: everyone
        </Text>
        <View style={{ flexDirection: 'row', gap: 10, flexWrap: 'wrap' }}>
          {[
            { key: 'alpha', label: 'Alpha', color: '#f87171', desc: 'Team only' },
            { key: 'beta', label: 'Beta', color: '#fbbf24', desc: 'Testers' },
            { key: 'live', label: 'Live', color: '#4ade80', desc: 'Everyone' },
          ].map(opt => (
            <TouchableOpacity
              key={opt.key}
              disabled={loading}
              onPress={() => setAccessStage(opt.key)}
              style={{
                paddingVertical: 10, paddingHorizontal: 18, borderRadius: 10,
                borderWidth: 2,
                borderColor: accessStage === opt.key ? opt.color : 'rgba(255,255,255,0.15)',
                backgroundColor: accessStage === opt.key ? `${opt.color}20` : 'transparent',
                alignItems: 'center',
              }}
            >
              <Text style={{ color: accessStage === opt.key ? opt.color : 'rgba(255,255,255,0.6)', fontWeight: '700', fontSize: 15 }}>{opt.label}</Text>
              <Text style={{ color: accessStage === opt.key ? opt.color : 'rgba(255,255,255,0.4)', fontSize: 11, marginTop: 2 }}>{opt.desc}</Text>
            </TouchableOpacity>
          ))}
        </View>
        <Text style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, marginTop: 10 }}>
          Current stage: <Text style={{ color: accessStage === 'live' ? '#4ade80' : accessStage === 'beta' ? '#fbbf24' : '#f87171', fontWeight: '700' }}>{accessStage?.toUpperCase()}</Text>
        </Text>
      </View>

      <View style={styles.row}>
        <Text style={styles.label}>Coming Soon Page (legacy)</Text>
        <Switch value={comingSoon} onValueChange={toggleComingSoon} disabled={loading} />
        <Text style={styles.hintText}>{comingSoon ? 'Enabled' : 'Disabled'}</Text>
      </View>

      <View style={styles.row}>
        <Text style={styles.label}>Maintenance Banner</Text>
        <Switch value={banner} onValueChange={toggleBanner} disabled={loading} />
        <Text style={styles.hintText}>{banner ? 'Shown' : 'Hidden'}</Text>
      </View>

      {banner ? (
        <Text style={styles.caption}>
          Started: {startedAt && startedAt.toDate ? startedAt.toDate().toLocaleString() : 'now'}
        </Text>
      ) : null}

      <Text style={styles.caption}>To grant access during Coming Soon, have users sign in; admins can access the full app after login.</Text>

      <Text style={styles.caption}>Coming Soon redirects non-admins (prod only). Banner shows on all pages with start time.</Text>
      
      <Text style={styles.sectionTitle}>Community Settings</Text>
      
      <View style={styles.row}>
        <Text style={styles.label}>Waitlist Modal</Text>
        <Switch value={communityModalEnabled} onValueChange={toggleCommunityModal} disabled={loading} />
        <Text style={styles.hintText}>{communityModalEnabled ? 'Show Modal' : 'Redirect to URL'}</Text>
      </View>

      {!communityModalEnabled ? (
        <View style={styles.row}>
          <Text style={styles.label}>Redirect URL</Text>
          <TextInput
            style={styles.input}
            value={communityRedirectUrl}
            onChangeText={setCommunityRedirectUrl}
            onBlur={updateCommunityUrl}
            placeholder="https://discord.gg/your-link"
          />
        </View>
      ) : null}

      <Text style={styles.caption}>
        Toggle the Community Screen on/off in Feature Flags. When off: modal or URL redirect (set above).
      </Text>
      
      <Text style={styles.sectionTitle}>SGP Optimizer Settings</Text>
      
      <View style={styles.row}>
        <Text style={styles.label}>SGP Coming Soon Modal</Text>
        <Switch value={sgpComingSoonEnabled} onValueChange={toggleSgpComingSoon} disabled={loading} />
        <Text style={styles.hintText}>{sgpComingSoonEnabled ? 'Shown' : 'Hidden'}</Text>
      </View>

      <Text style={styles.caption}>
        When enabled: Shows "Coming Soon" modal on SGP Optimizer page for non-admin users. When disabled: All users can access the SGP Optimizer.
      </Text>

      <Text style={styles.sectionTitle}>Image-to-query</Text>

      <View style={styles.row}>
        <Text style={styles.label}>Photo Upload (Image-to-Query)</Text>
        <Switch value={photoUploadEnabled} onValueChange={togglePhotoUpload} disabled={loading} />
        <Text style={styles.hintText}>{photoUploadEnabled ? 'Enabled' : 'Disabled'}</Text>
      </View>
      <Text style={styles.caption}>
        When enabled: Shows a camera icon on the search bar letting users upload a photo of a bet slip. The AI extracts a query from the image. Currently off by default — enable once the feature is ready for broader testing.
      </Text>
    </View>
  );
}

// ── PreviewTab ────────────────────────────────────────────────────────────────
// ─── Archive Mode Tab ──────────────────────────────────────────────────────────
// One toggle to rule them all: flips the entire app between Live API mode and
// Archive/Freeze mode. When frozen, all scheduled Cloud Functions skip their
// API calls and the SGP + odds serving layer uses the archived Firestore data
// with the freeze timestamp as "now" so nothing gets filtered out.
